"use client";

import { useEffect, useState, useTransition } from "react";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { useQueryStates } from "nuqs";
import { PackagePlusIcon, SearchXIcon } from "lucide-react";

import { NewIngredientDialog } from "@/components/create-ingredient/dialog";
import { LayoutPageH1 } from "@/components/primitives/layout";
import { SearchInput } from "@/components/primitives/search-input";
import { SingleBreadcrumb } from "@/components/primitives/single-breadcrumb";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getCategoryLabel, getCategoryList } from "@/lib/categories";
import { useDebouncedEffect } from "@/lib/hooks/useDebouncedEffect";
import { useIsClient } from "@/lib/hooks/useIsClient";
import type { Ingredient } from "@/lib/types";
import { sourceLabel } from "@/components/primitives/source-badge";
import { ActiveFilterChip } from "@/lib/search/recipe-search-controls";
import {
  buildIngredientDetailsHref,
  buildIngredientSearchHref,
} from "@/lib/ingredients/ingredientNavigation";
import { IngredientCard, IngredientCardSkeleton } from "./ingredient-card";
import { IngredientPagination } from "./ingredient-pagination";
import {
  ingredientSearchParams,
  INGREDIENT_SOURCE_FILTERS,
  type IngredientSourceFilter,
} from "./ingredient-search-params";

export const INGREDIENT_SEARCH_LIMIT = 12;

type SearchFilters = {
  q: string;
  category: ReturnType<typeof getCategoryList>[number] | null;
  source: IngredientSourceFilter | null;
};

function IngredientSearchGrid({
  ingredients,
  userId,
  filters,
  currentPage,
  onChange,
}: {
  ingredients: Ingredient[];
  userId: string | null;
  filters: SearchFilters;
  currentPage: number;
  onChange: () => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {ingredients.map((ingredient) => (
        <IngredientCard
          key={ingredient.id}
          ingredient={ingredient}
          detailsHref={buildIngredientDetailsHref({
            slug: ingredient.slug,
            q: filters.q,
            category: filters.category,
            source: filters.source,
            page: currentPage,
          })}
          editable={Boolean(userId && ingredient.createdBy === userId)}
          onDelete={onChange}
          onUpdate={onChange}
        />
      ))}
    </div>
  );
}

function SearchSkeletons() {
  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
      aria-busy="true"
      aria-label="Loading ingredients"
    >
      {Array.from({ length: INGREDIENT_SEARCH_LIMIT }).map((_, index) => (
        <IngredientCardSkeleton key={index} />
      ))}
    </div>
  );
}

function SearchEmptyState({
  filtered,
  onClear,
  onCreate,
  canClear,
}: {
  filtered: boolean;
  onClear: () => void;
  onCreate: () => void;
  canClear: boolean;
}) {
  if (filtered) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <SearchXIcon aria-hidden />
          </EmptyMedia>
          <EmptyTitle>No Ingredients Found</EmptyTitle>
          <EmptyDescription>
            Try another name or clear the filters to see more ingredients.
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button variant="outline" onClick={onClear} disabled={!canClear}>
            Clear All Filters
          </Button>
        </EmptyContent>
      </Empty>
    );
  }

  return (
    <Empty>
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <PackagePlusIcon aria-hidden />
        </EmptyMedia>
        <EmptyTitle>No Ingredients Yet</EmptyTitle>
        <EmptyDescription>
          Create a custom ingredient to start building your database.
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <NewIngredientDialog onCreate={onCreate}>
          <Button variant="outline">Create Ingredient</Button>
        </NewIngredientDialog>
      </EmptyContent>
    </Empty>
  );
}

function Filters({
  filters,
  setFilters,
}: {
  filters: SearchFilters;
  setFilters: ReturnType<
    typeof useQueryStates<typeof ingredientSearchParams>
  >[1];
}) {
  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-none">
        <Select
          value={filters.category ?? "all"}
          onValueChange={(category) =>
            setFilters((previous) => ({
              ...previous,
              category:
                category === "all"
                  ? null
                  : (getCategoryList().find(
                      (candidate) => candidate === category,
                    ) ?? null),
              page: null,
            }))
          }
        >
          <SelectTrigger aria-label="Filter by category" className="sm:w-44">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {getCategoryList().map((category) => (
              <SelectItem key={category} value={category}>
                {getCategoryLabel(category)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.source ?? "all"}
          onValueChange={(source) =>
            setFilters((previous) => ({
              ...previous,
              source:
                source === "all"
                  ? null
                  : (INGREDIENT_SOURCE_FILTERS.find(
                      (candidate) => candidate === source,
                    ) ?? null),
              page: null,
            }))
          }
        >
          <SelectTrigger aria-label="Filter by source" className="sm:w-44">
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {INGREDIENT_SOURCE_FILTERS.map((source) => (
              <SelectItem key={source} value={source}>
                {sourceLabel(source)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );
}

function ActiveFilters({
  filters,
  setFilters,
}: {
  filters: SearchFilters;
  setFilters: ReturnType<
    typeof useQueryStates<typeof ingredientSearchParams>
  >[1];
}) {
  if (!filters.category && !filters.source) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {filters.category ? (
        <ActiveFilterChip
          onRemove={() =>
            setFilters((previous) => ({
              ...previous,
              category: null,
              page: null,
            }))
          }
        >
          Category: {getCategoryLabel(filters.category)}
        </ActiveFilterChip>
      ) : null}
      {filters.source ? (
        <ActiveFilterChip
          onRemove={() =>
            setFilters((previous) => ({
              ...previous,
              source: null,
              page: null,
            }))
          }
        >
          Source: {sourceLabel(filters.source)}
        </ActiveFilterChip>
      ) : null}
      <Button
        type="button"
        variant="link"
        className="ml-1 text-xs"
        onClick={() => setFilters(null)}
      >
        Clear All
      </Button>
    </div>
  );
}

export function IngredientsSearchResults({
  ingredients,
  total,
  currentPage,
  userId,
}: {
  ingredients: Ingredient[];
  total: number;
  currentPage: number;
  userId: string | null;
}) {
  const router = useRouter();
  const isRouterLoading = useRouterState({
    select: (state) => state.isLoading,
  });
  const isClient = useIsClient();
  const [isPending, startTransition] = useTransition();
  const [filters, setFilters] = useQueryStates(ingredientSearchParams, {
    startTransition,
  });
  const [inputValue, setInputValue] = useState(filters.q);
  const filtered = Boolean(filters.q || filters.category || filters.source);
  const refresh = () => void router.invalidate();
  const totalPages = Math.ceil(total / INGREDIENT_SEARCH_LIMIT);

  useEffect(() => {
    setInputValue(filters.q);
  }, [filters.q]);

  useDebouncedEffect(
    () => {
      if (filters.q === inputValue) return;
      setFilters((previous) => ({
        ...previous,
        q: inputValue,
        page: null,
      }));
    },
    400,
    [inputValue],
  );

  const clearAll = () => setFilters(null);
  const resultLabel = total === 1 ? "ingredient" : "ingredients";
  const isLoading = !isClient || isPending || isRouterLoading;

  return (
    <>
      <section className="mb-8">
        <SingleBreadcrumb href="/ingredients" label="Back" />
        <LayoutPageH1 className="mb-4">Search Ingredients</LayoutPageH1>
        <div className="flex flex-col gap-2 sm:flex-row">
          <SearchInput
            value={inputValue}
            placeholder="Search ingredients…"
            onChange={(event) => setInputValue(event.target.value)}
            className="w-full sm:max-w-md sm:flex-1"
          />
          <Filters filters={filters} setFilters={setFilters} />
        </div>
        <div className="mt-3 flex flex-col gap-3">
          <ActiveFilters filters={filters} setFilters={setFilters} />
          <p
            className="text-muted-foreground text-sm tabular-nums"
            aria-live="polite"
          >
            {total.toLocaleString()} {resultLabel} found
          </p>
        </div>
      </section>

      <section className="mb-12">
        {isPending && ingredients.length === 0 ? (
          <SearchSkeletons />
        ) : ingredients.length === 0 ? (
          <SearchEmptyState
            filtered={filtered}
            onClear={clearAll}
            onCreate={refresh}
            canClear={isClient}
          />
        ) : (
          <div
            className={
              isLoading
                ? "space-y-8 opacity-60 transition-opacity"
                : "space-y-8 transition-opacity"
            }
            role="region"
            aria-label="Ingredient search results"
            aria-busy={isLoading}
          >
            <IngredientSearchGrid
              ingredients={ingredients}
              userId={userId}
              filters={filters}
              currentPage={currentPage}
              onChange={refresh}
            />
            <IngredientPagination
              currentPage={currentPage}
              totalPages={totalPages}
              getPageHref={(page) =>
                buildIngredientSearchHref({ ...filters, page })
              }
              className="justify-center"
            />
          </div>
        )}
      </section>
    </>
  );
}
